'use client'

import { EmailSidebarSingleMatterCard } from '@repo/ui/EmailSidebarSingleMatterCard'
import { EnterIcon } from '@repo/ui/EnterIcon'
import { MatterList } from '@repo/ui/MatterList'
import { useEffect } from 'react'
import { useEmailCardStore } from '@repo/store/useEmailCardStore'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useRef, useState } from 'react'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIIconButton from '@mui/material/IconButton'
import MUIInput from '@mui/material/Input'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

type EmailSidebarMatterSearchAndSelectProps = {
  testData?: boolean
  selectedMatter?: any
  onSelect?: () => void
  onClear?: () => void
  sx?: React.CSSProperties
}

export const EmailSidebarMatterSearchAndSelect = ({
  selectedMatter,
  onSelect,
  onClear,
  testData = false,
  sx
}: EmailSidebarMatterSearchAndSelectProps) => {
  const searchEmailCardRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [resultsAreForSearchTerm, setResultsAreForSearchTerm] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [searchResults, setSearchResults] = useState(null)
  const [showRecent, setShowRecent] = useState(true)
  const [includeArchived, setIncludeArchived] = useState(false)
  const isLoading = useLoadingStore((state: any) => state.isLoading)
  const { currentClientMatterNo, matters, fetchMatterSearch, recentMatters, fetchRecentMatters } = useMattersStore()

  useEffect(() => {
    if (testData) return

    fetchRecentMatters()
  }, [fetchRecentMatters])

  useEffect(() => {
    if (searchTerm) handleSearch()
  }, [includeArchived])

  const handleSearch = async () => {
    if (!searchTerm) {
      setSearchResults(null)
      setShowRecent(true)
      return
    }

    try {
      setShowRecent(false)
      setIsProcessing(true)
      const { matterSearchResults, searchQueriesStillInProgress } = await fetchMatterSearch({
        searchCriteria: searchTerm,
        includeArchived: includeArchived
      })

      setResultsAreForSearchTerm(searchTerm || '')

      if (!matterSearchResults) {
        // this might happen if multiple requests come back out of sequence; we only return a value for the last one, so ignore the others
        return
      }

      setSearchResults(matterSearchResults)
      setIsProcessing(searchQueriesStillInProgress)
    } catch (err) {
      console.error('Error fetching matter search results:', err)
      setIsProcessing(false)
      setSearchResults(null)
    }
  }

  return (
    <div data-testid="EmailSidebarMatterSearchAndSelect">
      {selectedMatter ? (
        <EmailSidebarSingleMatterCard matter={selectedMatter} onClear={onClear} sx={sx} />
      ) : (
        <MUIStack
          sx={{
            '[data-testid="miniMatterCardButton"]': { height: '60px', minHeight: '64px' },
            '[data-testid="miniMatterCard"]': { padding: '8px' },
            '[data-testid="avatar"]': { marginRight: '4px', width: '36px', height: '36px' },
            '[data-testid="miniMatterCardIcons"]': { display: 'none' },
            '[data-testid="clientNameAndDescription"]': { paddingRight: '14px' },
            ...sx
          }}
        >
          <MUIStack direction="column" justifyContent="flex-start" alignItems="flex-start" sx={{ height: '75px' }}>
            <MUIInput
              size="small"
              name="emailCardMatterSearch"
              sx={{
                mt: 0,
                width: '100%',
                input: {
                  backgroundColor: 'background.dark',
                  height: '40px',
                  padding: '0 0 0 14px',
                  borderRadius: '6px',
                  position: 'relative'
                },
                '&:after': {
                  content: 'none'
                },
                '&:before': {
                  content: 'none'
                },
                '.MuiInputBase-root': {
                  borderRadius: '6px',
                  height: '40px',
                  paddingRight: 0,
                  backgroundColor: 'background.dark',
                  '&:after': {
                    content: 'none'
                  }
                },
                'fieldset, .Mui-focused fieldset': {
                  border: 'none'
                }
              }}
              placeholder="Search all matters"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
              inputProps={{
                ref: searchEmailCardRef,
                maxLength: 80
              }}
              endAdornment={
                <MUIInputAdornment position="end" sx={{ position: 'absolute', right: 0 }}>
                  <MUIIconButton
                    onClick={handleSearch}
                    disabled={!searchTerm}
                    sx={{
                      borderRadius: '0 6px 6px 0',
                      height: '40px',
                      backgroundColor: searchTerm ? 'background.slate100' : 'transparent',
                      opacity: 1,
                      cursor: searchTerm ? 'pointer' : 'not-allowed',
                      transition: 'all 250ms',
                      alignItems: 'center'
                    }}
                  >
                    <MUITypography
                      variant="h6"
                      sx={{
                        mb: 0,
                        mr: 2,
                        pointerEvents: 'none',
                        width: '100%',
                        paddingLeft: '0px',
                        fontSize: '12px',
                        fontWeight: '400',
                        textTransform: 'uppercase'
                      }}
                    >
                      Search
                    </MUITypography>
                    <EnterIcon
                      sx={{
                        height: '24px',
                        width: '24px',
                        color: searchTerm ? 'white' : 'grey',
                        cursor: searchTerm ? 'pointer' : 'not-allowed',
                        transition: 'all 250ms'
                      }}
                    />
                  </MUIIconButton>
                </MUIInputAdornment>
              }
            />
            <MUIFormControlLabel
              control={<MUICheckbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
              label="Include archived"
            />
          </MUIStack>

          <MUIStack
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            sx={{ width: '100%', height: '100%', marginTop: 6 }}
          >
            <div style={{ width: '100%', height: '100%' }}>
              {showRecent ? (
                <>
                  {renderLabel({ label: `Recent matters` })}
                  <MatterList
                    matters={recentMatters}
                    minHeight="360px"
                    maxHeight="360px"
                    size="small"
                    isLoading={isLoading}
                    isEmpty={!recentMatters?.length}
                    clickHandler={onSelect}
                    showLoadingState={false}
                    isEmailCard={true}
                  />
                </>
              ) : null}

              {!showRecent ? (
                <>
                  <MUIStack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                    {renderLabel({ label: `Results for '${resultsAreForSearchTerm}'` })}
                    <MUITypography
                      variant="body1"
                      onClick={() => {
                        setSearchTerm('')
                        setShowRecent(true)
                      }}
                      sx={{
                        mt: 0,
                        mb: 1,
                        mr: 2,
                        cursor: 'pointer',
                        color: 'secondary.main',
                        paddingRight: '16px',
                        fontSize: '12px',
                        fontWeight: '400'
                      }}
                    >
                      Clear search
                    </MUITypography>
                  </MUIStack>
                  <MatterList
                    matters={searchResults}
                    minHeight="420px"
                    maxHeight="420px"
                    isLoading={isLoading || isProcessing}
                    // @ts-ignore
                    isEmpty={!searchResults?.length}
                    clickHandler={onSelect}
                    isEmailCard={true}
                  />
                </>
              ) : null}
            </div>
          </MUIStack>
        </MUIStack>
      )}
    </div>
  )
}

const renderLabel = ({ label, paddingLeft = '0' }: renderLabelType) => {
  return (
    <MUITypography
      variant="h6"
      sx={{
        mb: 1,
        mt: 0,
        pointerEvents: 'none',
        paddingLeft,
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}
    >
      {label}
    </MUITypography>
  )
}

type renderLabelType = {
  label: string
  paddingLeft?: string
}
